import { isPlatformBrowser } from '@angular/common';
import {
  computed,
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { SysWarehouse } from '../../shared/models/warehouse.model';
import { WarehouseRoleId } from '@shared/constants/common.const';
import { PermissionNode } from '../models/permission.model';
import { User } from '../models/user.model';

@Injectable({
  providedIn: 'root',
})
export class AuthStore {
  private readonly platformId = inject(PLATFORM_ID);

  // State
  private readonly _user = signal<User | null>(null);
  private readonly _token = signal<string | null>(
    isPlatformBrowser(this.platformId)
      ? localStorage.getItem('webadmin_token')
      : null,
  );
  private readonly _menuTree = signal<PermissionNode[]>([]);
  private readonly _isLoaded = signal<boolean>(false);

  // Selectors
  readonly user = this._user.asReadonly();
  readonly token = this._token.asReadonly();
  readonly menuTree = this._menuTree.asReadonly();
  readonly isLoaded = this._isLoaded.asReadonly();
  readonly isAuthenticated = computed(() => !!this._token());
  readonly isWarehouseUser = computed(
    () => this._user()?.role_id === WarehouseRoleId,
  );
  readonly initialWarehouse = computed<SysWarehouse | null>(() => {
    const user = this._user();
    if (!user || user.role_id !== WarehouseRoleId) {
      return null;
    }
    return {
      id: user.id,
      full_name: user.full_name || '',
      address: '',
      agent_type: 0,
      status: 0,
      user_name: '',
      password: '',
      role_id: WarehouseRoleId,
      group_permission_id: 0,
      is_active: true,
    };
  });

  /**
   * Bản đồ quyền (permissionId -> PermissionNode) để tra cứu O(1)
   */
  readonly permissionMap = computed(() => {
    const map = new Map<string, PermissionNode>();
    const processNodes = (nodes: PermissionNode[]) => {
      for (const node of nodes) {
        map.set(node.id, node);
        if (node.children) processNodes(node.children);
      }
    };
    processNodes(this._menuTree());
    return map;
  });

  // Actions
  setUser(user: User | null) {
    this._user.set(user);
    if (user) {
      this._isLoaded.set(true);
    }
  }

  setToken(token: string | null) {
    this._token.set(token);
    if (token) {
      localStorage.setItem('webadmin_token', token);
    } else {
      localStorage.removeItem('webadmin_token');
    }
  }

  setMenuTree(menu: PermissionNode[]) {
    this._menuTree.set(menu);
  }

  clearAll() {
    this._user.set(null);
    this._token.set(null);
    this._menuTree.set([]);
    this._isLoaded.set(false);
    localStorage.removeItem('webadmin_token');
  }

  // ========== Permission Checks ==========
  public canInsert(id: string): boolean {
    const permission = this.permissionMap().get(id);
    return permission ? permission.is_insert > 0 : false;
  }

  public canUpdate(id: string): boolean {
    const permission = this.permissionMap().get(id);
    return permission ? permission.is_update > 0 : false;
  }

  public canDelete(id: string): boolean {
    const permission = this.permissionMap().get(id);
    return permission ? permission.is_delete > 0 : false;
  }

  public canExport(id: string): boolean {
    const permission = this.permissionMap().get(id);
    return permission ? permission.is_export > 0 : false;
  }

  public canPrint(id: string): boolean {
    const permission = this.permissionMap().get(id);
    return permission ? permission.is_print > 0 : false;
  }
}
